import React, { useEffect, useMemo, useState } from 'react';
import { Bot, ChevronDown, Square } from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';
import { useAuthStore } from '../../stores/authStore';
import type { AsyncJobMetadata } from '../../utils/asyncJobs';
import { getAsyncJobMetadata, isAsyncJobActiveStatus } from '../../utils/asyncJobs';
import { formatAbsoluteMessageTime, formatMessageTime } from '../../utils/formatMessageTime';
import { cn, ui } from '../../utils';
import { useStickyBottom } from '../../hooks/useStickyBottom';
import AsyncJobCard from './AsyncJobCard';
import StreamingMessage from './StreamingMessage';
import TypingIndicator from './TypingIndicator';
import GroupMessageBubble from './GroupMessageBubble';
import MessageBubble from './MessageBubble';

interface RoutingSuggestionPayload {
  agentId: string;
  agentName?: string;
  userQuestion: string;
  reason?: string;
}

interface MessageListProps {
  conversationId: string;
  highlightedMessageId?: string | null;
  className?: string;
  onSuggestionSelect?: (text: string) => void;
  onRoutingSuggestionSelect?: (suggestion: RoutingSuggestionPayload) => void;
}

function getRoutingSuggestion(message: any): RoutingSuggestionPayload | null {
  const suggestion = message?.metadata && typeof message.metadata === 'object'
    ? (message.metadata as Record<string, any>).routingSuggestion
    : null;

  if (!suggestion || typeof suggestion !== 'object') {
    return null;
  }

  const agentId = String(suggestion.agentId || '').trim();
  const userQuestion = String(suggestion.userQuestion || '').trim();

  if (!agentId || !userQuestion) {
    return null;
  }

  return {
    agentId,
    agentName: typeof suggestion.agentName === 'string' ? suggestion.agentName : undefined,
    userQuestion,
    reason: typeof suggestion.reason === 'string' ? suggestion.reason : undefined,
  };
}

function getAsyncJob(message: any): AsyncJobMetadata | null {
  return getAsyncJobMetadata(message ?? null);
}

export function MessageList({ conversationId, highlightedMessageId = null, className = '', onSuggestionSelect, onRoutingSuggestionSelect }: MessageListProps) {
  const messages = useChatStore((state) => state.messages[conversationId] || []);
  const isLoading = useChatStore((state) => state.isLoading);
  const error = useChatStore((state) => state.error);
  const agents = useChatStore((state) => state.agents);
  const conversations = useChatStore((state) => state.conversations);
  const streamingMessageId = useChatStore((state) => state.streamingMessageIds[conversationId]);
  const streamingTokenCount = useChatStore((state) =>
    streamingMessageId ? (state.streamingTokens[streamingMessageId] || []).length : 0,
  );
  const isTyping = useChatStore((state) => state.isTyping || Boolean(state.typing[conversationId]));
  const toggleReaction = useChatStore((state) => state.toggleReaction);
  const togglePin = useChatStore((state) => state.togglePin);
  const retryMessage = useChatStore((state) => state.retryMessage);
  const regenerateMessage = useChatStore((state) => state.regenerateMessage);
  const editMessage = useChatStore((state) => state.editMessage);
  const rateMessage = useChatStore((state) => state.rateMessage);
  const stopGenerating = useChatStore((state) => state.stopGenerating);
  const deleteMessage = useChatStore((state) => state.deleteMessage);
  const messagePaging = useChatStore((state) => state.messagePaging?.[conversationId]);
  const isLoadingOlder = useChatStore((state) => Boolean(state.loadingOlderMessages?.[conversationId]));
  const loadOlderMessages = useChatStore((state) => state.loadOlderMessages);
  const currentUser = useAuthStore((state) => state.impersonatedUser ?? state.user);
  const [timeReference, setTimeReference] = useState(() => Date.now());
  const [flashMessageId, setFlashMessageId] = useState<string | null>(null);

  const pinnedMessages = useMemo(() => messages.filter((message) => message.isPinned), [messages]);
  const regularMessages = useMemo(() => messages.filter((message) => !message.isPinned), [messages]);
  const lastMessage = regularMessages[regularMessages.length - 1] ?? messages[messages.length - 1] ?? null;
  const activeStreamingMessage = useMemo(
    () => [...regularMessages].reverse().find((message) => message.role === 'assistant' && message.status === 'streaming') ?? null,
    [regularMessages],
  );
  const hasActiveAsyncJob = useMemo(
    () => regularMessages.some((message) => {
      const asyncJob = getAsyncJob(message);
      return Boolean(asyncJob && isAsyncJobActiveStatus(asyncJob.status));
    }),
    [regularMessages],
  );
  const hasLiveTimeUpdates = useMemo(
    () => hasActiveAsyncJob || Boolean(activeStreamingMessage),
    [activeStreamingMessage, hasActiveAsyncJob],
  );
  const lastAssistantMessageId = useMemo(
    () => [...regularMessages].reverse().find((message) => message.role === 'assistant')?.id ?? null,
    [regularMessages],
  );

  const conversation = useMemo(
    () => conversations.find((item) => item.id === conversationId) ?? null,
    [conversationId, conversations],
  );
  const agent = useMemo(
    () => (conversation?.isGroup ? null : agents.find((item) => item.id === conversation?.agentId) ?? agents[0] ?? null),
    [agents, conversation?.agentId, conversation?.isGroup],
  );

  const dependencyKey = useMemo(
    () => [
      conversationId,
      regularMessages.map((message) => {
        const asyncJob = getAsyncJob(message);
        return `${message.id}:${message.status || ''}:${message.content.length}:${message.deliveredAt || ''}:${message.error || ''}:${asyncJob?.progressMessage || ''}:${asyncJob?.elapsedMs || 0}`;
      }).join('|'),
      pinnedMessages.length,
      lastMessage?.id || '',
      lastMessage?.content.length || 0,
      lastMessage?.status || '',
      streamingMessageId || '',
      streamingTokenCount,
      isTyping && !hasActiveAsyncJob ? 'typing' : '',
    ].join('::'),
    [conversationId, hasActiveAsyncJob, isTyping, lastMessage?.content.length, lastMessage?.id, lastMessage?.status, pinnedMessages.length, regularMessages, streamingMessageId, streamingTokenCount],
  );

  const {
    containerRef,
    endRef,
    hasNewMessages,
    handleScroll,
    resumeStickyBottom,
  } = useStickyBottom({
    conversationId,
    dependencyKey,
  });

  // Wave 4: incremental history — when the user scrolls near the top of a
  // paged conversation, fetch the next-older page and keep the viewport
  // anchored on the messages they were reading.
  const loadingOlderRef = React.useRef(false);
  const handleScrollWithPaging = React.useCallback(() => {
    handleScroll();

    const container = containerRef.current;
    if (!container || !messagePaging?.hasMore || isLoadingOlder || loadingOlderRef.current) {
      return;
    }
    if (container.scrollTop > 120) {
      return;
    }

    loadingOlderRef.current = true;
    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;

    void loadOlderMessages(conversationId)
      .then((loaded) => {
        if (!loaded) {
          return;
        }
        requestAnimationFrame(() => {
          const current = containerRef.current;
          if (current) {
            current.scrollTop = previousScrollTop + (current.scrollHeight - previousScrollHeight);
          }
        });
      })
      .finally(() => {
        loadingOlderRef.current = false;
      });
  }, [containerRef, conversationId, handleScroll, isLoadingOlder, loadOlderMessages, messagePaging?.hasMore]);

  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    const interval = window.setInterval(() => {
      setTimeReference(Date.now());
    }, hasLiveTimeUpdates ? 1_000 : 60_000);

    return () => window.clearInterval(interval);
  }, [hasLiveTimeUpdates, messages.length]);

  useEffect(() => {
    if (!highlightedMessageId || !containerRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const messageElement = containerRef.current?.querySelector<HTMLElement>(`[data-message-id="${highlightedMessageId}"]`);
      if (!messageElement) {
        return;
      }

      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlashMessageId(highlightedMessageId);
      window.setTimeout(() => setFlashMessageId((current) => current === highlightedMessageId ? null : current), 2500);
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [containerRef, highlightedMessageId, messages.length]);

  if (error && messages.length === 0) {
    return (
      <div className={`flex flex-1 items-center justify-center p-8 ${className}`}>
        <div className="rounded-[1.75rem] border border-red-500/20 bg-red-500/10 px-6 py-5 text-center shadow-sm shadow-red-950/10">
          <div className="mb-2 text-red-300">Unable to load this conversation</div>
          <div className="text-sm text-muted-foreground">{error}</div>
        </div>
      </div>
    );
  }

  if (isLoading && messages.length === 0) {
    return (
      <div className={`flex flex-1 items-center justify-center p-8 ${className}`}>
        <div className="text-center text-muted-foreground">
          <div className="ally-spinner-ring mx-auto mb-3 h-7 w-7" />
          <div>Loading conversation...</div>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className={`flex flex-1 items-center justify-center p-8 ${className}`}>
        <div className={cn(ui.emptyState, 'max-w-md px-8 py-10')}>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600/15 text-blue-300">
            <Bot className="h-7 w-7" />
          </div>
          <h3 className="text-xl font-semibold text-foreground-bright">
            {conversation?.isGroup ? `Start the group chat in ${conversation.title || 'this room'}` : `Start chatting with ${agent?.name || 'Polaris'}`}
          </h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {conversation?.isGroup
              ? 'Mention one or more agents with @name to route your message.'
              : 'Ask a question, paste a task, or start a new thread. Your recent chats stay in the sidebar.'}
          </p>
        </div>
      </div>
    );
  }

  const renderPinnedSummaryTime = (timestamp?: string) => {
    const source = timestamp || new Date(timeReference).toISOString();
    return formatMessageTime(source, timeReference);
  };

  const wrapperClassName = (messageId: string) => cn(
    'rounded-2xl transition-colors duration-500',
    flashMessageId === messageId && 'bg-blue-500/10 ring-1 ring-blue-400/40',
  );

  return (
    <div className={cn('relative flex min-h-0 flex-1', className)}>
      <div
        ref={containerRef}
        onScroll={handleScrollWithPaging}
        className="messages-scroll flex-1 min-h-0 overflow-y-auto px-2.5 py-3 sm:px-4 sm:py-4 md:px-5 md:py-5"
      >
        <div className="mx-auto flex max-w-4xl flex-col gap-3 pb-20 sm:pb-24 md:pb-16">
          {messagePaging?.hasMore ? (
            <div className="flex justify-center py-2 text-xs text-muted-foreground">
              {isLoadingOlder ? (
                <span className="flex items-center gap-2"><span className="ally-spinner-ring h-4 w-4" /> Loading older messages…</span>
              ) : (
                <button
                  type="button"
                  className="rounded-full border border-border/60 px-3 py-1 hover:bg-muted/40"
                  onClick={() => void loadOlderMessages(conversationId)}
                >
                  Load older messages ({Math.max(0, (messagePaging.total || 0) - messages.length)} more)
                </button>
              )}
            </div>
          ) : null}
          {pinnedMessages.length > 0 ? (
            <div className="rounded-[1.25rem] border border-amber-500/20 bg-amber-500/10 p-4 shadow-sm shadow-amber-950/10">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-amber-200">Pinned messages</div>
              <div className="space-y-3">
                {pinnedMessages.map((message) => (
                  <div
                    key={`pinned-${message.id}`}
                    data-message-id={message.id}
                    className={cn('rounded-xl border border-amber-400/20 bg-background/50 px-3 py-3', flashMessageId === message.id && 'ring-1 ring-blue-400/50')}
                  >
                    <div className="mb-1 flex items-center justify-between gap-3 text-xs text-amber-100/80">
                      <span>{message.role === 'user' ? currentUser?.name || 'You' : agents.find((item) => item.id === message.agentId)?.name || agent?.name || 'Assistant'}</span>
                      <span title={formatAbsoluteMessageTime(message.createdAt || message.timestamp)}>{renderPinnedSummaryTime(message.createdAt || message.timestamp)}</span>
                    </div>
                    <div className="line-clamp-3 text-sm text-foreground">{message.content || 'Pinned message'}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {regularMessages.map((message, index) => {
            const asyncJob = getAsyncJob(message);
            if (asyncJob) {
              const agentLabel = conversation?.agentName || agent?.name || 'Agent';

              return (
                <div key={message.id} data-message-id={message.id} className={wrapperClassName(message.id)}>
                  <AsyncJobCard
                    asyncJob={asyncJob}
                    agentLabel={agentLabel}
                    agentAvatar={conversation?.agentAvatar || agent?.avatar}
                    timeReference={timeReference}
                  />
                </div>
              );
            }

            if (conversation?.isGroup) {
              const sender = message.role === 'user'
                ? {
                    id: message.userId || currentUser?.id || 'user',
                    name: message.user?.name || currentUser?.name || 'You',
                    avatar: currentUser?.avatar,
                    type: 'user' as const,
                  }
                : {
                    id: message.agentId || 'assistant',
                    name: agents.find((item) => item.id === message.agentId)?.name || 'Assistant',
                    avatar: agents.find((item) => item.id === message.agentId)?.avatar,
                    type: 'agent' as const,
                  };
              const previous = regularMessages[index - 1];
              const isGrouped = Boolean(
                previous &&
                previous.role === message.role &&
                (previous.role === 'user'
                  ? (previous.userId || previous.user?.name) === (message.userId || message.user?.name)
                  : previous.agentId === message.agentId),
              );

              const routingSuggestion = getRoutingSuggestion(message);
              const shouldShowSuggestions = message.role === 'assistant'
                && message.id === lastAssistantMessageId
                && message.status !== 'streaming'
                && (message.suggestions?.length ?? 0) > 0
                && typeof onSuggestionSelect === 'function';
              const shouldShowRoutingSuggestion = message.role === 'assistant'
                && message.id === lastAssistantMessageId
                && message.status !== 'streaming'
                && routingSuggestion
                && typeof onRoutingSuggestionSelect === 'function';

              return (
                <div key={message.id} data-message-id={message.id} className={wrapperClassName(message.id)}>
                  <GroupMessageBubble
                    message={message}
                    sender={sender}
                    isGrouped={isGrouped}
                    timeReference={timeReference}
                    onRetry={retryMessage}
                    onDismiss={deleteMessage}
                  />
                  {shouldShowSuggestions ? (
                    <div className="mt-2 pl-0 sm:pl-12">
                      <div className="suggestions-scroll flex flex-wrap gap-2">
                        {(message.suggestions ?? []).map((suggestion) => (
                          <button
                            key={`${message.id}-${suggestion}`}
                            type="button"
                            onClick={() => onSuggestionSelect?.(suggestion)}
                            className={cn(ui.button.base, ui.button.chip, 'rounded-full px-3 py-2 text-xs')}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {shouldShowRoutingSuggestion && routingSuggestion ? (
                    <div className="mt-2 pl-0 sm:pl-12">
                      <button
                        type="button"
                        onClick={() => onRoutingSuggestionSelect?.(routingSuggestion)}
                        className="flex min-h-[56px] w-full items-center justify-between gap-3 rounded-[1.25rem] border border-blue-500/25 bg-blue-500/10 px-4 py-3 text-left transition-colors duration-150 hover:border-blue-400/50 hover:bg-blue-500/15"
                      >
                        <div>
                          <div className="text-sm font-medium text-blue-100">💡 {routingSuggestion.agentName || routingSuggestion.agentId} might be able to help with this</div>
                          <div className="mt-1 text-xs text-blue-200/80">{routingSuggestion.reason || 'Start a new conversation with the specialist agent.'}</div>
                        </div>
                        <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-blue-200">Start conversation</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            }

            const routingSuggestion = getRoutingSuggestion(message);
            const shouldShowSuggestions = message.role === 'assistant'
              && message.id === lastAssistantMessageId
              && message.status !== 'streaming'
              && (message.suggestions?.length ?? 0) > 0
              && typeof onSuggestionSelect === 'function';
            const shouldShowRoutingSuggestion = message.role === 'assistant'
              && message.id === lastAssistantMessageId
              && message.status !== 'streaming'
              && routingSuggestion
              && typeof onRoutingSuggestionSelect === 'function';

            return (
              <div key={message.id} data-message-id={message.id} className={wrapperClassName(message.id)}>
                <MessageBubble
                  message={message}
                  isUser={message.role === 'user'}
                  isLast={index === regularMessages.length - 1}
                  agent={agent}
                  timeReference={timeReference}
                  onReact={toggleReaction}
                  onPin={togglePin}
                  onRetry={retryMessage}
                  onDismiss={deleteMessage}
                  onRegenerate={regenerateMessage}
                  onEdit={message.role === 'user' && !conversation?.isGroup ? editMessage : undefined}
                  onRate={message.role === 'assistant' && !conversation?.isGroup ? rateMessage : undefined}
                />
                {shouldShowSuggestions ? (
                  <div className="mt-2 pl-0 sm:pl-12">
                    <div className="suggestions-scroll flex flex-wrap gap-2">
                      {(message.suggestions ?? []).map((suggestion) => (
                        <button
                          key={`${message.id}-${suggestion}`}
                          type="button"
                          onClick={() => onSuggestionSelect?.(suggestion)}
                          className={cn(ui.button.base, ui.button.chip, 'rounded-full px-3 py-2 text-xs')}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {shouldShowRoutingSuggestion && routingSuggestion ? (
                  <div className="mt-2 pl-0 sm:pl-12">
                    <button
                      type="button"
                      onClick={() => onRoutingSuggestionSelect?.(routingSuggestion)}
                      className="flex min-h-[56px] w-full items-center justify-between gap-3 rounded-[1.25rem] border border-blue-500/25 bg-blue-500/10 px-4 py-3 text-left transition-colors duration-150 hover:border-blue-400/50 hover:bg-blue-500/15"
                    >
                      <div>
                        <div className="text-sm font-medium text-blue-100">💡 {routingSuggestion.agentName || routingSuggestion.agentId} might be able to help with this</div>
                        <div className="mt-1 text-xs text-blue-200/80">{routingSuggestion.reason || 'Start a new conversation with the specialist agent.'}</div>
                      </div>
                      <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-blue-200">Start conversation</span>
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}

          {streamingMessageId && !regularMessages.some((message) => message.id === streamingMessageId) ? (
            <StreamingMessage conversationId={conversationId} />
          ) : null}
          {activeStreamingMessage ? (
            <div className="flex justify-start pl-12">
              <button
                type="button"
                onClick={() => stopGenerating(conversationId)}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-amber-400/30 bg-card/95 px-3 py-2 text-sm font-medium text-amber-200 transition-colors duration-150 hover:border-amber-300/50 hover:bg-background"
              >
                <Square className="h-3.5 w-3.5 fill-current" />
                Stop generating
              </button>
            </div>
          ) : null}
          {isTyping && !hasActiveAsyncJob ? <TypingIndicator agent={agent} /> : null}
          <div ref={endRef} />
        </div>
      </div>

      {hasNewMessages ? (
        <div className="pointer-events-none absolute bottom-4 right-4 z-30 sm:bottom-5 sm:right-5">
          <button
            type="button"
            onClick={resumeStickyBottom}
            className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-400/25 bg-card/95 text-blue-200 shadow-2xl shadow-black/20 transition-colors duration-150 hover:border-blue-300/50 hover:bg-background"
            title="Scroll to latest messages"
            aria-label="Scroll to latest messages"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default MessageList;
